import React from 'react';
import {
  Button,
  Card,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronUp, Eye, Lock, RefreshCcw, Zap } from 'lucide-react';
import { SetupRecord, SetupStackSummary, StackCandidate } from '../../types/deployment';
import { StackRunHistoryPanel } from './StackRunHistoryPanel';
import { SetupRunHistoryState } from './SetupHistoryPanel';
import {
  SETUP_STATUS_COLORS,
  SETUP_STATUS_LABELS,
  formatDryRunSummary,
  formatRelativeTime,
  getStackRunPhases,
  getLockTargetLabels,
} from './setupExplorerUtils';

interface ServerStackSectionProps {
  stackSummaries: SetupStackSummary[];
  saving?: boolean;
  historyStateByKey: Record<string, SetupRunHistoryState | undefined>;
  openHistoryByKey: Record<string, boolean | undefined>;
  onToggleHistory: (summary: SetupStackSummary) => void;
  onLoadMoreHistory: (summary: SetupStackSummary) => void;
  onRefreshHistory: (summary: SetupStackSummary) => void;
  onOpenStack: (stack: StackCandidate) => void;
  onRetryStack: (stackRunId: string) => void;
  onOpenRunDetails: (setup: SetupRecord) => void;
}

export const ServerStackSection: React.FC<ServerStackSectionProps> = ({
  stackSummaries,
  saving = false,
  historyStateByKey,
  openHistoryByKey,
  onToggleHistory,
  onLoadMoreHistory,
  onRefreshHistory,
  onOpenStack,
  onRetryStack,
  onOpenRunDetails,
}) => {
  if (stackSummaries.length === 0) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ px: 2, py: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Zap size={16} />
          <Typography variant="subtitle1" fontWeight={700}>Stack Orchestration</Typography>
        </Stack>

        <Stack spacing={1.25}>
          {stackSummaries.map((summary) => {
            const latestRun = summary.latestRun;
            const phases = latestRun ? getStackRunPhases(latestRun) : [];
            const lockSummary = summary.activeLocks[0] ?? null;
            const lockTargetLabels = lockSummary ? getLockTargetLabels(lockSummary) : [];

            return (
              <Card key={summary.key} variant="outlined" sx={{ px: 1.5, py: 1.25 }}>
                <Stack spacing={1.25}>
                  <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ lg: 'center' }}
                    spacing={1.5}
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body1" fontWeight={700}>
                          {summary.label}
                        </Typography>
                        <Chip size="small" variant="outlined" label={summary.environment} />
                        {summary.historyCount > 0 ? (
                          <Chip size="small" variant="outlined" label={`${summary.historyCount} historical`} />
                        ) : null}
                        {summary.activeLocks.length > 0 ? (
                          <Tooltip title={lockSummary ? `Locked by ${lockSummary.owner?.name || lockSummary.owner?.email || 'another operator'}${lockTargetLabels.length > 0 ? ` for ${lockTargetLabels.join(', ')}` : ''}` : 'Another server action is running'}>
                            <Chip icon={<Lock size={12} />} label="Locked" size="small" color="warning" />
                          </Tooltip>
                        ) : null}
                        {latestRun ? (
                          <Chip
                            size="small"
                            color={SETUP_STATUS_COLORS[latestRun.status] || 'default'}
                            label={SETUP_STATUS_LABELS[latestRun.status] || latestRun.status}
                          />
                        ) : null}
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        {latestRun
                          ? `${latestRun.currentStep || latestRun.failedStep || 'No active phase'} • Started ${formatRelativeTime(latestRun.startedAt ?? latestRun.createdAt)}`
                          : 'No stack runs yet'}
                      </Typography>

                      {latestRun?.metadata?.dryRun?.summary ? (
                        <Typography variant="caption" color="text.secondary">
                          Dry run: {formatDryRunSummary(latestRun.metadata?.dryRun?.summary)}
                        </Typography>
                      ) : null}
                      {phases.slice(0, 2).map((phase) => (
                        <Typography key={`${summary.key}-${phase.index}`} variant="caption" color="text.secondary">
                          {phase.name}: {phase.status || 'pending'}
                          {phase.failureClass ? ` (${phase.failureClass})` : ''}
                        </Typography>
                      ))}
                    </Stack>

                    <Stack direction="row" gap={1} flexShrink={0} flexWrap="wrap" useFlexGap>
                      {summary.candidate ? (
                        <Tooltip title={summary.activeLocks.length > 0 ? 'This server already has an active lock.' : 'Run the next phased stack action.'}>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<Zap size={14} />}
                              disabled={saving || summary.activeLocks.length > 0}
                              onClick={() => onOpenStack(summary.candidate as StackCandidate)}
                            >
                              Run Stack
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                      {latestRun ? (
                        <>
                          <Tooltip title="View full details">
                            <span>
                              <IconButton size="small" onClick={() => onOpenRunDetails(latestRun)}>
                                <Eye size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {latestRun.status === 'failed' && latestRun.actionType === 'stack-run' ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<RefreshCcw size={14} />}
                              disabled={saving}
                              onClick={() => onRetryStack(latestRun._id)}
                            >
                              Retry Failed Phase
                            </Button>
                          ) : null}
                        </>
                      ) : null}
                    </Stack>
                  </Stack>

                  <Button
                    size="small"
                    variant="text"
                    endIcon={openHistoryByKey[summary.key] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    onClick={() => onToggleHistory(summary)}
                  >
                    {openHistoryByKey[summary.key] ? 'Hide History' : 'View History'}
                  </Button>
                  <Collapse in={Boolean(openHistoryByKey[summary.key])} unmountOnExit>
                    <StackRunHistoryPanel
                      historyState={historyStateByKey[summary.key]}
                      onLoadMore={() => onLoadMoreHistory(summary)}
                      onOpenRunDetails={onOpenRunDetails}
                      onRefreshHistory={() => onRefreshHistory(summary)}
                      saving={saving}
                    />
                  </Collapse>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
};
